import {
  Inject,
  Injectable,
  ModuleWithProviders,
  NgModule,
} from '@angular/core';
import {
  addDocumentMetadata,
  Backend,
  BackendConfigs,
  BackendPushResponse,
  BackendSuccessResponse,
  BACKEND_DEFAULT_BATCH_SIZE,
  DatastoreCollectionType,
  DatastorePushCollectionType,
  DatastoreSetCollectionType,
  deepSpread,
  mergeRawDocuments,
  OrderByDirection,
  Ordering,
  PushDocumentType,
  RecursivePartial,
  Reference,
  StoreBackend,
  StoreBackendInterface,
} from '@freelancer/datastore/core';
import * as Rx from 'rxjs';
import {
  PushPropagator,
  PushTransformer,
} from './datastore-testing-controller';
import { DatastoreFakeConfig, DATASTORE_FAKE_CONFIG } from './datastore.config';
import { debugConsoleLog, debugConsoleWarn } from './datastore.helpers';
import { FakeStoreState } from './store.model';

@Injectable()
export class StoreBackendFake implements StoreBackendInterface {
  // FIXME: Consider saving this in session storage
  private storeStateSubject$: Rx.BehaviorSubject<
    FakeStoreState
  > = new Rx.BehaviorSubject({});
  storeState$ = this.storeStateSubject$.asObservable();

  // This isn't typed wonderfully. Ideally it would know what type it maps to.
  pushTransformers: Map<
    string,
    PushTransformer<DatastoreCollectionType & DatastorePushCollectionType>
  > = new Map();
  mutationPropagators: ReadonlyArray<
    PushPropagator<
      DatastoreCollectionType & DatastorePushCollectionType,
      DatastoreCollectionType & DatastorePushCollectionType
    >
  > = [];

  constructor(
    @Inject(DATASTORE_FAKE_CONFIG) private datastoreConfig: DatastoreFakeConfig,
  ) {}

  defaultOrder<C extends DatastoreCollectionType>(
    collection: C['Name'],
  ): Ordering<C> {
    return [
      {
        field: 'id' as any,
        direction: OrderByDirection.DESC,
      },
    ];
  }

  batchSize<C extends DatastoreCollectionType>(ref: Reference<C>): number {
    return BACKEND_DEFAULT_BATCH_SIZE;
  }

  /** Pushes a document without transforming it or pushing to related collections. */
  pushRaw<C extends DatastoreCollectionType & DatastorePushCollectionType>(
    ref: Reference<C>,
    document: C['DocumentType'],
  ): Rx.Observable<BackendPushResponse<C>> {
    debugConsoleLog(
      this.datastoreConfig,
      `Pushing to '${ref.path.collection}' raw document`,
      document,
    );

    this.pushInternal(ref, document);

    return Rx.of({
      status: 'success',
      id: (document as any).id,
    });
  }

  push<C extends DatastoreCollectionType & DatastorePushCollectionType>(
    ref: Reference<C>,
    document: PushDocumentType<C> & Partial<Pick<C['DocumentType'], 'id'>>,
  ): Rx.Observable<BackendPushResponse<C>> {
    debugConsoleLog(
      this.datastoreConfig,
      `Pushing to '${ref.path.collection}' document`,
      document,
    );

    const refCollection = ref.path.collection;
    const collectionsToPush = this.getTargetCollections(
      refCollection,
      this.mutationPropagators,
    );

    let refDocumentId: string | number | undefined;
    collectionsToPush.forEach(collection => {
      if (collection !== refCollection) {
        debugConsoleLog(
          this.datastoreConfig,
          `Pushing to '${collection}' document which is related to '${refCollection}'`,
          document,
        );
      }

      const pushTransformer = this.getPushTransformer(
        refCollection,
        collection,
        this.pushTransformers,
        this.mutationPropagators,
      );

      const transformedDocument = pushTransformer(document);
      if (collection === refCollection) {
        refDocumentId = transformedDocument.id;
      }

      this.pushInternal(
        { ...ref, path: { ...ref.path, collection } },
        transformedDocument,
      );
    });

    return Rx.of({
      status: 'success',
      id: (refDocumentId as any) || 0,
    });
  }

  private pushInternal<
    C extends DatastoreCollectionType & DatastorePushCollectionType
  >(
    { path: { collection, authUid } }: Reference<C>,
    document: C['DocumentType'],
  ): void {
    const storeState = this.storeStateSubject$.getValue();
    const collectionSlice = storeState[collection];

    if (collectionSlice === undefined) {
      this.storeStateSubject$.next({
        ...storeState,
        [collection]: {
          [authUid]: {
            documents: addDocumentMetadata([document]),
          },
        },
      });
    } else {
      const userCollectionSlice = collectionSlice[authUid];

      if (userCollectionSlice === undefined) {
        this.storeStateSubject$.next({
          ...storeState,
          [collection]: {
            ...collectionSlice,
            [authUid]: {
              documents: addDocumentMetadata([document]),
            },
          },
        });
      } else {
        this.storeStateSubject$.next({
          ...storeState,
          [collection]: {
            ...collectionSlice,
            [authUid]: {
              documents: mergeRawDocuments(
                userCollectionSlice.documents,
                addDocumentMetadata([document]),
              ),
            },
          },
        });
      }
    }
  }

  set<C extends DatastoreCollectionType & DatastoreSetCollectionType>(
    { path: { collection, authUid } }: Reference<C>,
    id: number | string,
    document: C['DocumentType'],
  ): Rx.Observable<BackendSuccessResponse> {
    debugConsoleLog(
      this.datastoreConfig,
      `Setting '${id}' in '${collection}' document`,
      document,
    );

    const storeState = this.storeStateSubject$.getValue();
    const collectionSlice = storeState[collection];

    if (collectionSlice === undefined) {
      this.storeStateSubject$.next({
        ...storeState,
        [collection]: {
          [authUid]: {
            documents: addDocumentMetadata([document]),
          },
        },
      });
    } else {
      const userCollectionSlice = collectionSlice[authUid];

      if (userCollectionSlice === undefined) {
        this.storeStateSubject$.next({
          ...storeState,
          [collection]: {
            ...collectionSlice,
            [authUid]: {
              documents: addDocumentMetadata([document]),
            },
          },
        });
      } else {
        this.storeStateSubject$.next({
          ...storeState,
          [collection]: {
            ...collectionSlice,
            [authUid]: {
              documents: mergeRawDocuments(
                userCollectionSlice.documents,
                addDocumentMetadata([document]),
              ),
            },
          },
        });
      }
    }

    return Rx.of({ status: 'success' });
  }

  /**
   * Merges a delta to the document, as well as the same delta to any documents
   * in related collections.
   */
  update<C extends DatastoreCollectionType>(
    { path: { collection: refCollection, authUid } }: Reference<C>,
    id: number | string,
    delta: RecursivePartial<C['DocumentType']>,
  ): Rx.Observable<BackendSuccessResponse> {
    debugConsoleLog(
      this.datastoreConfig,
      `Updating '${id}' in ${refCollection} with delta`,
      delta,
    );

    const collectionsToUpdate = this.getTargetCollections(
      refCollection,
      this.mutationPropagators,
    );

    collectionsToUpdate.forEach(collection => {
      if (collection !== refCollection) {
        debugConsoleLog(
          this.datastoreConfig,
          `Updating '${id}' in ${collection} which is related to '${refCollection}' with delta`,
          delta,
        );
      }

      const storeState = this.storeStateSubject$.getValue();
      const collectionSlice = storeState[collection];
      if (collectionSlice === undefined) {
        debugConsoleWarn(
          this.datastoreConfig,
          "Trying to merge into a collection that doesn't exist. Skipping",
        );
        return Rx.of({ status: 'success' });
      }

      const userCollectionSlice = collectionSlice[authUid];
      if (userCollectionSlice === undefined) {
        debugConsoleWarn(
          this.datastoreConfig,
          "Trying to merge into a collection that doesn't exist for this user. Skipping",
        );
        return Rx.of({ status: 'success' });
      }

      const originalDocument = userCollectionSlice.documents[id].rawDocument;

      this.storeStateSubject$.next({
        ...storeState,
        [collection]: {
          ...collectionSlice,
          [authUid]: {
            documents: mergeRawDocuments(
              userCollectionSlice.documents,
              addDocumentMetadata([deepSpread(originalDocument, delta)]),
            ),
          },
        },
      });
    });

    return Rx.of({ status: 'success' });
  }

  delete<C extends DatastoreCollectionType>(
    { path: { collection, authUid } }: Reference<C>,
    id: number | string,
  ): Rx.Observable<BackendSuccessResponse> {
    debugConsoleLog(
      this.datastoreConfig,
      `Deleting '${id}' from ${collection}.`,
    );

    const storeState = this.storeStateSubject$.getValue();
    const collectionSlice = storeState[collection];

    if (collectionSlice === undefined) {
      debugConsoleWarn(
        this.datastoreConfig,
        `Trying to delete from '${collection}' which doesn't exist. Skipping`,
      );
      return Rx.of({ status: 'success' });
    }

    const userCollectionSlice = collectionSlice[authUid];
    if (userCollectionSlice === undefined) {
      debugConsoleWarn(
        this.datastoreConfig,
        `Trying to delete from '${collection}' which doesn't exist for user ${authUid}. Skipping`,
      );
      return Rx.of({ status: 'success' });
    }

    const documents = { ...userCollectionSlice.documents };
    delete documents[id];

    this.storeStateSubject$.next({
      ...storeState,
      [collection]: {
        ...collectionSlice,
        [authUid]: {
          documents,
        },
      },
    });
    return Rx.of({ status: 'success' });
  }

  /**
   * Clears the state, push transformers and resets errors.
   */
  reset<C extends DatastoreCollectionType>(
    authUid: string,
    collection?: C['Name'],
  ) {
    const storeState = this.storeStateSubject$.getValue();

    if (!collection) {
      debugConsoleLog(
        this.datastoreConfig,
        'Resetting the store to an empty state. You can ignore any "missing collection/document" warnings after this message.',
      );
      this.pushTransformers.clear();
      this.mutationPropagators = [];
      this.storeStateSubject$.next({});
      return;
    }

    const collectionSlice = storeState[collection];
    if (collectionSlice === undefined) {
      debugConsoleWarn(
        this.datastoreConfig,
        `Trying to reset '${collection}' which doesn't exist. Skipping`,
      );
      return;
    }

    this.storeStateSubject$.next(
      collection
        ? {
            ...storeState,
            [collection]: {
              ...collectionSlice,
              [authUid]: {
                documents: {},
              },
            },
          }
        : {},
    );
  }

  addFeature<C extends DatastoreCollectionType>(
    collectionName: any, // FIXME
    requestFactory: BackendConfigs[any], // FIXME
  ): void {
    // Used by BackendFeatureModule but not implemented since there's no backend
  }

  // Get a list of collections to update.
  private getTargetCollections<C extends DatastoreCollectionType>(
    originalCollection: C['Name'],
    mutationPropagators: ReadonlyArray<
      PushPropagator<
        DatastoreCollectionType & DatastorePushCollectionType,
        DatastoreCollectionType & DatastorePushCollectionType
      >
    >,
  ): ReadonlyArray<C['Name']> {
    const targets = new Set([originalCollection]);
    mutationPropagators.forEach(propagator => {
      if (propagator.from === originalCollection) {
        targets.add(propagator.to);
      }
    });
    return Array.from(targets);
  }

  private getPushTransformer<
    C1 extends DatastoreCollectionType,
    C2 extends DatastoreCollectionType
  >(
    originalCollection: C1['Name'],
    targetCollection: C2['Name'],
    pushTransformers: Map<
      string,
      PushTransformer<DatastoreCollectionType & DatastorePushCollectionType>
    >,
    mutationPropagators: ReadonlyArray<
      PushPropagator<
        DatastoreCollectionType & DatastorePushCollectionType,
        DatastoreCollectionType & DatastorePushCollectionType
      >
    >,
  ) {
    // Use the propagator transformer instead of the push transformer if it has
    // been specified between two different collections
    if (originalCollection !== targetCollection) {
      const propagator = mutationPropagators.find(
        p => p.from === originalCollection && p.to === targetCollection,
      );

      if (!propagator) {
        debugConsoleWarn(
          this.datastoreConfig,
          `Missing propagator from '${originalCollection}' to '${targetCollection}'`,
        );
        throw new Error(
          `Missing propagator from '${originalCollection}' to '${targetCollection}'`,
        );
      }

      return propagator.transformer;
    }

    const transformer = pushTransformers.get(targetCollection);
    if (!transformer) {
      // toPromise() on a `createDocument` call seems to swallow errors
      console.error(
        this.datastoreConfig,
        `Missing push transformer for collection '${targetCollection}'`,
      );
      throw new Error(
        `Missing push transformer for collection '${targetCollection}'`,
      );
    }
    return transformer;
  }
}

type BackendConfigFactory<C extends DatastoreCollectionType> = () => Backend<C>;

@NgModule({})
export class BackendRootModule {}

@NgModule({})
export class BackendFeatureModule {}

@NgModule({})
export class BackendFakeModule {
  static forRoot(): ModuleWithProviders<BackendRootModule> {
    return {
      ngModule: BackendRootModule,
      providers: [
        StoreBackendFake,
        { provide: StoreBackend, useExisting: StoreBackendFake }, // BackendModule.forFeature
      ],
    };
  }

  static forFeature<C extends DatastoreCollectionType>(
    collectionName: C['Name'],
    configFactory: BackendConfigFactory<C>,
  ): ModuleWithProviders<BackendFeatureModule> {
    return {
      ngModule: BackendFeatureModule,
    };
  }
}
